/**
 * Plan limits (spec §9) — config, not hard-coded branches.
 *
 * "One post published to one page = 1 post", so a post going to three pages
 * costs three. Drafts, regenerations and skips never cost anything;
 * regenerations are capped by `maxRegenerationsPerPost` instead.
 */
/**
 * Stands in for "no ceiling".
 *
 * `Number.MAX_SAFE_INTEGER` rather than `Infinity`, because these limits are
 * serialised to the dashboard as JSON and `JSON.stringify(Infinity)` is
 * `null` — the limit would arrive as "unknown" and every comparison against
 * it would silently become false. A large finite number survives the wire and
 * still makes every `used < limit` check true.
 */
export const UNLIMITED = Number.MAX_SAFE_INTEGER;
/** True for a limit that should be shown as "Unlimited" rather than a number. */
export function isUnlimited(limit) {
    return limit >= UNLIMITED;
}
/** Rewrites allowed per post on every tier that has a ceiling. */
export const MAX_REGENERATIONS_PER_POST = 3;
/**
 * Every paid tier's monthly allowance was doubled at launch, and Free with
 * them — 3→6, 35→70, 150→300, 450→900.
 *
 * Free is doubled deliberately rather than held back. Six posts is roughly a
 * fortnight of posting instead of three days, which is long enough to see the
 * thing work on a real audience; three was long enough to see it exist. The
 * conversion argument for a free tier is that it proves the product, and a
 * tier too small to prove anything just produces people who tried it once.
 *
 * `previousPostsPerMonth` keeps the old figure next to the new one so the
 * pricing page reads them both from here.
 */
export const PLANS = {
    free: {
        key: "free",
        previousPostsPerMonth: 3,
        name: "Free",
        maxBrands: 1,
        maxPages: 1,
        postsPerMonth: 6,
        maxProductsPerBrand: 5,
        canBuyPacks: false,
        watermark: true,
        teamRoles: false,
        maxRegenerationsPerPost: MAX_REGENERATIONS_PER_POST,
    },
    solo: {
        key: "solo",
        previousPostsPerMonth: 35,
        name: "Solo",
        maxBrands: 1,
        maxPages: 4,
        postsPerMonth: 70,
        maxProductsPerBrand: 25,
        canBuyPacks: true,
        watermark: false,
        teamRoles: false,
        maxRegenerationsPerPost: MAX_REGENERATIONS_PER_POST,
    },
    pro: {
        key: "pro",
        previousPostsPerMonth: 150,
        name: "Pro",
        maxBrands: 5,
        maxPages: 20,
        postsPerMonth: 300,
        maxProductsPerBrand: 100,
        canBuyPacks: true,
        watermark: false,
        teamRoles: false,
        maxRegenerationsPerPost: MAX_REGENERATIONS_PER_POST,
    },
    agency: {
        key: "agency",
        previousPostsPerMonth: 450,
        name: "Agency",
        maxBrands: 15,
        maxPages: 60,
        postsPerMonth: 900,
        maxProductsPerBrand: 300,
        canBuyPacks: true,
        watermark: false,
        teamRoles: true,
        maxRegenerationsPerPost: MAX_REGENERATIONS_PER_POST,
    },
    unlimited: {
        key: "unlimited",
        previousPostsPerMonth: null,
        name: "Unlimited",
        maxBrands: UNLIMITED,
        maxPages: UNLIMITED,
        postsPerMonth: UNLIMITED,
        maxProductsPerBrand: UNLIMITED,
        canBuyPacks: true,
        watermark: false,
        teamRoles: true,
        maxRegenerationsPerPost: UNLIMITED,
    },
};
/**
 * WhatsApp approval, campaigns, Plan-my-month and human-like scheduling are on
 * EVERY plan including Free (spec §7, §9) — plans differ only by volume. Kept
 * as a constant so no later phase quietly gates one of them behind a tier.
 */
export const FEATURES_ON_ALL_PLANS = [
    "whatsapp_approval",
    "campaigns",
    "plan_my_month",
    "human_like_scheduling",
];
/** Post packs — one-time Paddle purchases. Sizes/prices live in config. */
export const POST_PACKS = [
    { key: "pack_10", posts: 10 },
    { key: "pack_25", posts: 25 },
    { key: "pack_50", posts: 50 },
];
/** Purchased packs roll over for a year; monthly allowance never rolls over. */
export const PACK_EXPIRY_MONTHS = 12;
export function planFor(key) {
    return PLANS[key] ?? PLANS.free;
}
//# sourceMappingURL=plans.js.map